/**
 * OrderId Value Object
 * Ensures order identifiers are valid and provides type safety
 */
export class OrderId {
  private static readonly ORDER_ID_PREFIX = 'order_';
  private static readonly MINIMUM_ID_LENGTH = 5;

  private constructor(private readonly uniqueIdentifier: string) {}

  static fromExistingId(existingOrderId: string): OrderId {
    const normalizedOrderId = this.validateAndNormalizeOrderId(existingOrderId);
    return new OrderId(normalizedOrderId);
  }

  static generateNewOrderId(): OrderId {
    const timestampComponent = this.createTimestampComponent();
    const randomComponent = this.createRandomComponent();
    const generatedOrderId = `${OrderId.ORDER_ID_PREFIX}${timestampComponent}_${randomComponent}`;

    return new OrderId(generatedOrderId);
  }

  getUniqueIdentifier(): string {
    return this.uniqueIdentifier;
  }

  isEqualTo(otherOrderId: OrderId): boolean {
    return this.uniqueIdentifier === otherOrderId.uniqueIdentifier;
  }

  formatForDisplay(): string {
    return this.uniqueIdentifier.toUpperCase();
  }

  private static validateAndNormalizeOrderId(orderId: string): string {
    const normalizedOrderId = orderId?.trim();

    if (!normalizedOrderId) {
      throw new Error('Order ID cannot be empty or null');
    }

    if (normalizedOrderId.length < OrderId.MINIMUM_ID_LENGTH) {
      throw new Error(`Order ID must be at least ${OrderId.MINIMUM_ID_LENGTH} characters long`);
    }

    return normalizedOrderId;
  }

  private static createTimestampComponent(): string {
    return Date.now().toString(36);
  }

  private static createRandomComponent(): string {
    return Math.random().toString(36).substring(2, 8);
  }
}
